import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Clock, MapPin, ShoppingBag, Ticket, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { pub, money, errMsg } from "@/lib/api";
import { HeaderLogo } from "@/components/Brand";
import { PoweredBy, Spinner, PracticeBanner } from "@/components/Layout";
import { ItemDialog } from "@/components/ItemDialog";
import { CartSheet } from "@/components/CartSheet";

function useCart(slug) {
  const key = `lg_cart_${slug}`;
  const [cart, setCart] = useState(() => JSON.parse(localStorage.getItem(key) || "[]"));
  useEffect(() => { localStorage.setItem(key, JSON.stringify(cart)); }, [cart, key]);
  const add = (line) => setCart((c) => {
    const ex = c.find((l) => l.key === line.key);
    return ex ? c.map((l) => (l.key === line.key ? { ...l, qty: l.qty + line.qty } : l)) : [...c, line];
  });
  const setQty = (k, q) => setCart((c) => (q <= 0 ? c.filter((l) => l.key !== k) : c.map((l) => (l.key === k ? { ...l, qty: q } : l))));
  return { cart, add, setQty };
}

const VenueHero = ({ v }) => (
  <div className="bg-brand-pale px-4 pb-6 pt-5">
    <div className="mx-auto max-w-2xl">
      <div className="flex items-center gap-4">
        {v.logo ? <img src={v.logo} alt="" className="h-16 w-16 rounded-2xl object-cover" /> :
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-brand font-heading text-2xl font-black text-white">{v.name[0]}</div>}
        <div>
          <h1 className="text-3xl font-black leading-tight" data-testid="venue-name">{v.name}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm">
            <span className={`rounded-full px-3 py-0.5 font-bold ${v.accepting_orders ? "bg-brand text-white" : "bg-white text-ink"}`} data-testid="venue-open-badge">{v.accepting_orders ? "Open" : v.is_open ? "Not taking orders" : "Closed"}</span>
            {v.is_demo && <span className="rounded-full bg-brand-light px-3 py-0.5 font-bold text-ink" data-testid="venue-demo-badge">DEMO · Stripe test mode</span>}
            <span className="flex items-center gap-1 text-muted2"><Clock size={14} /> ~{v.prep_minutes} min</span>
          </div>
        </div>
      </div>
      {v.address && <p className="mt-3 flex items-center gap-1 text-sm text-muted2"><MapPin size={14} /> {v.address}{v.hours ? ` · ${v.hours}` : ""}</p>}
      {!v.accepting_orders && <div className="mt-4 flex items-center gap-2 rounded-2xl bg-white p-3 text-sm font-semibold" data-testid="venue-not-accepting"><AlertCircle size={16} className="text-muted2" />{v.not_accepting_reason}</div>}
    </div>
  </div>
);

const MenuItem = ({ it, onOpen, disabled }) => (
  <button disabled={it.sold_out || disabled} onClick={() => onOpen(it)} data-testid={`menu-item-${it.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    className="card-lg flex w-full items-center justify-between gap-4 p-4 text-left transition-colors hover:border-brand-mid disabled:opacity-60">
    <div>
      <div className="font-heading text-lg font-extrabold">{it.name}</div>
      {it.description && <div className="text-sm text-muted2">{it.description}</div>}
      <div className="mt-1 font-semibold">{money(it.price_cents)}{it.options.length ? "+" : ""}
        {it.tax_exempt && <span className="ml-2 rounded-full bg-brand-pale px-2 py-0.5 text-xs">tax exempt</span>}</div>
    </div>
    {it.sold_out ? <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-bold">Sold out</span> :
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand text-xl font-bold text-white">+</span>}
  </button>
);

export default function VenuePage() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const [v, setV] = useState(null);
  const [err, setErr] = useState(null);
  const [item, setItem] = useState(null);
  const [open, setOpen] = useState(false);
  const { cart, add, setQty } = useCart(slug);

  useEffect(() => { pub.get(`/venues/${slug}`).then((r) => { setV(r.data); document.title = `${r.data.name} · LineGone`; }).catch((e) => setErr(errMsg(e))); }, [slug]);
  useEffect(() => { if (params.get("cancelled")) toast("Payment cancelled — your cart is still here."); }, [params]);

  if (err) return <div className="p-10 text-center" data-testid="venue-error">{err}</div>;
  if (!v) return <Spinner />;
  const count = cart.reduce((a, l) => a + l.qty, 0);
  const sum = cart.reduce((a, l) => a + l.qty * l.unit_cents, 0);
  return (
    <div className="min-h-screen pb-28" data-testid="venue-page">
      <header className="flex h-14 items-center justify-between border-b border-line bg-white px-4"><HeaderLogo className="h-6" /><span className="text-xs text-muted2">No app needed</span></header>
      {v.practice && <PracticeBanner text="PRACTICE ORDER: no real payment. Staff training mode — pay with a Stripe test card." testid="venue-practice-banner" />}
      <VenueHero v={v} />
      <main className="mx-auto max-w-2xl px-4">
        {v.events?.length > 0 && <section className="mt-6">
          <div className="eyebrow mb-2">Events</div>
          {v.events.map((e) => <Link key={e.id} to={`/e/${e.id}`} className="card-lg mb-2 flex items-center gap-3 p-4 hover:border-brand-mid" data-testid={`venue-event-link-${e.id}`}>
            <Ticket className="text-brand" /><div><div className="font-heading font-extrabold">{e.name}</div><div className="text-sm text-muted2">{e.date} · {e.time}</div></div></Link>)}
        </section>}
        {v.menu.length > 0 && <nav className="no-scrollbar sticky top-0 z-10 -mx-4 mt-4 flex gap-2 overflow-x-auto bg-white px-4 py-3">
          {v.menu.map((c) => <a key={c.id} href={`#cat-${c.id}`} className="whitespace-nowrap rounded-full bg-brand-pale px-4 py-2 text-sm font-bold">{c.name}</a>)}
        </nav>}
        {v.menu.map((c) => <section key={c.id} id={`cat-${c.id}`} className="mt-4 scroll-mt-16">
          <h2 className="mb-3 text-xl font-black">{c.name}</h2>
          <div className="grid gap-3">{c.items.map((it) => <MenuItem key={it.id} it={it} onOpen={setItem} disabled={!v.accepting_orders} />)}</div>
        </section>)}
        <PoweredBy />
      </main>
      {count > 0 && <div className="fixed inset-x-0 bottom-0 z-20 border-t border-line bg-white p-3">
        <button onClick={() => setOpen(true)} className="btn-primary mx-auto flex w-full max-w-2xl justify-between !py-4" data-testid="view-cart-btn">
          <span className="flex items-center gap-2"><ShoppingBag size={18} /> View order · {count}</span><span>{money(sum)}</span>
        </button>
      </div>}
      {item && <ItemDialog item={item} onClose={() => setItem(null)} onAdd={(l) => { add(l); setItem(null); toast.success(`${l.name} added`); }} />}
      <CartSheet open={open} onOpenChange={setOpen} venue={v} cart={cart} setQty={setQty} />
    </div>
  );
}
